import { ExternalLink, Pencil, Plus, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { ConfirmDialog } from '@/components/patterns/confirm-dialog';
import { DetalheItem, DetalheLista } from '@/components/patterns/metric';
import { PermissionButton } from '@/components/patterns/permission-button';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { SeveridadeBadge, StatusIncidenteBadge } from '@/components/patterns/incidente-badges';
import { StatusBuildBadge } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { CompilacoesHttpError, mensagemDoErro } from '@/data/http';
import type { IncidenteDetalhe, StatusIncidente } from '@/data/types';
import { useEventos } from '@/hooks/use-eventos';
import { useResource } from '@/hooks/use-resource';
import { useSessao } from '@/hooks/use-sessao';
import { emitir } from '@/lib/eventos';
import { formatarDataHora, formatarRelativo, plural, texto } from '@/lib/format';
import { acoesDeStatusIncidente, podeApagarIncidente, podeEscreverIncidente } from '@/lib/incidentes';
import { cn } from '@/lib/utils';
import { IncidenteDialog } from './IncidenteDialog';
import { LinhaDoTempoIncidente } from './linha-do-tempo-incidente';
import { VersoesSeletor } from './VersoesSeletor';
import { rotuloDoLink } from './rotulo-link';

function textoDoErro(causa: unknown): string {
  return mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa));
}

/**
 * Detalhe de um incidente — o mesmo componente no Sheet (`?incidente=<id>`) e
 * na rota `/incidentes/:id`. Quem monta decide o elemento do título.
 */
export function IncidenteDetalheView({
  id,
  titulo,
  aoApagar,
}: {
  id: number;
  titulo: (texto: string, className: string) => React.ReactNode;
  /** Chamado depois de apagar (o Sheet fecha). */
  aoApagar?: () => void;
}) {
  const recurso = useResource((signal) => dataSource.obterIncidente(id, signal), [id]);
  const { definir } = recurso;

  // O status de uma versão afetada mudou em outra tela: o badge dela aqui acompanha.
  useEventos((evento) => {
    if (evento.tipo === 'build' && recurso.dados?.versoes.some((v) => v.versao === evento.build.versao)) {
      recurso.atualizarEmSilencio();
    }
  });

  if (recurso.estado === 'carregando' && !recurso.dados) return <DetalheCarregando />;

  if (recurso.estado === 'erro' || !recurso.dados) {
    const rotulo = `Incidente #${id}`;
    if (recurso.erro instanceof CompilacoesHttpError && recurso.erro.status === 404) {
      return (
        <>
          {titulo(rotulo, 'sr-only')}
          <EstadoVazio
            titulo={`${rotulo} não encontrado`}
            descricao="Ele pode ter sido apagado. Confira a lista de incidentes."
            acao={
              <Link to="/incidentes" className="text-body font-bold text-primary underline-offset-4 hover:underline">
                Ver todos os incidentes
              </Link>
            }
          />
        </>
      );
    }
    return (
      <>
        {titulo(rotulo, 'sr-only')}
        <EstadoErro erro={recurso.erro} aoTentarDeNovo={recurso.recarregar} />
      </>
    );
  }

  return <Conteudo incidente={recurso.dados} titulo={titulo} aoAtualizar={definir} aoApagar={aoApagar} />;
}

function Conteudo({
  incidente,
  titulo,
  aoAtualizar,
  aoApagar,
}: {
  incidente: IncidenteDetalhe;
  titulo: (texto: string, className: string) => React.ReactNode;
  aoAtualizar: (detalhe: IncidenteDetalhe) => void;
  aoApagar?: () => void;
}) {
  const { roles } = useSessao();
  const escrita = podeEscreverIncidente(roles);
  const apagar = podeApagarIncidente(roles);

  const [executando, setExecutando] = React.useState<StatusIncidente | null>(null);
  const [erro, setErro] = React.useState<string | null>(null);
  const [editando, setEditando] = React.useState(false);
  const [adicionando, setAdicionando] = React.useState(false);
  const [removendo, setRemovendo] = React.useState<string | null>(null);
  const [apagando, setApagando] = React.useState(false);
  const [enviando, setEnviando] = React.useState(false);
  const [erroDialogo, setErroDialogo] = React.useState<string | null>(null);

  const versoes = incidente.versoes.map((v) => v.versao);
  const ultima = incidente.versoes.length <= 1;

  function atualizado(detalhe: IncidenteDetalhe, antes: string[] = versoes) {
    aoAtualizar(detalhe);
    emitir({ tipo: 'incidente', id: detalhe.id, versoes: [...new Set([...antes, ...detalhe.versoes.map((v) => v.versao)])] });
  }

  async function mudarStatus(destino: StatusIncidente, rotulo: string) {
    setExecutando(destino);
    setErro(null);
    try {
      const detalhe = await dataSource.editarIncidente(incidente.id, { status: destino });
      atualizado(detalhe);
      toast.success(`Incidente #${incidente.id}: ${rotulo.toLowerCase()}.`);
    } catch (causa) {
      setErro(textoDoErro(causa));
    } finally {
      setExecutando(null);
    }
  }

  async function remover(versao: string) {
    setEnviando(true);
    setErroDialogo(null);
    try {
      const detalhe = await dataSource.removerVersaoDoIncidente(incidente.id, versao);
      atualizado(detalhe);
      toast.success(`Versão ${versao} removida do incidente.`);
      setRemovendo(null);
    } catch (causa) {
      setErroDialogo(textoDoErro(causa));
    } finally {
      setEnviando(false);
    }
  }

  async function apagarIncidente() {
    setEnviando(true);
    setErroDialogo(null);
    try {
      await dataSource.apagarIncidente(incidente.id);
      emitir({ tipo: 'incidente', id: incidente.id, versoes });
      toast.success(`Incidente #${incidente.id} apagado.`);
      setApagando(false);
      aoApagar?.();
    } catch (causa) {
      setErroDialogo(textoDoErro(causa));
    } finally {
      setEnviando(false);
    }
  }

  const acoes = acoesDeStatusIncidente(incidente.status);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="cmp-eyebrow text-primary">Incidente #{incidente.id}</p>
        {titulo(incidente.titulo, 'text-section-title')}
        <div className="flex flex-wrap items-center gap-2">
          <SeveridadeBadge severidade={incidente.severidade} />
          <StatusIncidenteBadge status={incidente.status} />
          <span className="text-cell text-on-surface-muted">
            {plural(incidente.total_versoes, 'versão afetada', 'versões afetadas')}
          </span>
        </div>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Ações do incidente">
          {acoes.map((acao) => (
            <PermissionButton
              key={acao.destino}
              permitido={escrita.permitido}
              motivo={escrita.motivo}
              size="sm"
              variant={acao.principal ? 'primary' : 'outline'}
              loading={executando === acao.destino}
              disabled={executando !== null}
              onClick={() => void mudarStatus(acao.destino, acao.rotulo)}
            >
              {acao.rotulo}
            </PermissionButton>
          ))}
          <span className="mx-1 hidden h-6 w-px bg-border sm:inline-block" aria-hidden="true" />
          <PermissionButton
            permitido={escrita.permitido}
            motivo={escrita.motivo}
            size="sm"
            variant="outline"
            onClick={() => setEditando(true)}
          >
            <Pencil aria-hidden="true" />
            Editar
          </PermissionButton>
          <PermissionButton
            permitido={apagar.permitido}
            motivo={apagar.motivo}
            size="sm"
            variant="outline"
            className={apagar.permitido ? 'text-danger' : undefined}
            onClick={() => {
              setErroDialogo(null);
              setApagando(true);
            }}
          >
            <Trash2 aria-hidden="true" />
            Apagar
          </PermissionButton>
        </div>
        {erro ? (
          <Callout tom="erro" acao={<Button size="sm" variant="ghost" onClick={() => setErro(null)}>Dispensar</Button>}>
            {erro}
          </Callout>
        ) : null}
      </div>

      <section aria-labelledby="inc-descricao" className="flex flex-col gap-2">
        <h2 id="inc-descricao" className="text-card-title">
          Descrição
        </h2>
        {incidente.descricao.trim() ? (
          <p className="whitespace-pre-wrap break-words text-body">{incidente.descricao}</p>
        ) : (
          <p className="text-body text-on-surface-muted">Sem descrição.</p>
        )}
      </section>

      <section aria-label="Metadados do incidente">
        <DetalheLista className="grid-cols-1 gap-y-3 sm:grid-cols-2">
          <DetalheItem rotulo="Registrado por">
            {incidente.criado_por} ·{' '}
            <time dateTime={incidente.criado_em} title={formatarDataHora(incidente.criado_em)}>
              {formatarRelativo(incidente.criado_em)}
            </time>
          </DetalheItem>
          <DetalheItem rotulo="Atualizado">
            <time dateTime={incidente.atualizado_em} title={formatarDataHora(incidente.atualizado_em)}>
              {formatarRelativo(incidente.atualizado_em)}
            </time>
          </DetalheItem>
          <DetalheItem rotulo="Ticket">
            {incidente.link ? (
              <a
                href={incidente.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-full items-center gap-1 break-all text-primary underline-offset-4 hover:underline"
              >
                {rotuloDoLink(incidente.link)}
                <ExternalLink className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">(abre em nova aba)</span>
              </a>
            ) : (
              <span className="text-on-surface-muted">Sem ticket</span>
            )}
          </DetalheItem>
          <DetalheItem rotulo="Corrigido em">
            {incidente.corrigido_em ? (
              <span className="font-mono">{incidente.corrigido_em}</span>
            ) : (
              <span className="text-on-surface-muted">Sem versão de correção</span>
            )}
          </DetalheItem>
          {incidente.status === 'resolvido' ? (
            <DetalheItem rotulo="Resolvido por">
              {texto(incidente.resolvido_por)} ·{' '}
              <time dateTime={incidente.resolvido_em ?? undefined} title={formatarDataHora(incidente.resolvido_em)}>
                {formatarRelativo(incidente.resolvido_em)}
              </time>
            </DetalheItem>
          ) : null}
        </DetalheLista>
      </section>

      <section aria-labelledby="inc-versoes" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="inc-versoes" className="text-card-title">
            Versões afetadas
          </h2>
          <span className="cmp-eyebrow">{incidente.versoes.length}</span>
          <div className="ml-auto">
            <PermissionButton
              permitido={escrita.permitido}
              motivo={escrita.motivo}
              size="sm"
              variant="outline"
              onClick={() => {
                setErroDialogo(null);
                setAdicionando(true);
              }}
            >
              <Plus aria-hidden="true" />
              Adicionar versões
            </PermissionButton>
          </div>
        </div>

        <ul className="flex flex-col divide-y divide-border-subtle rounded-input border border-border">
          {incidente.versoes.map((versao) => (
            <li key={versao.versao} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
              <Link
                to={`/versoes/${encodeURIComponent(versao.versao)}`}
                className="font-mono text-body font-bold text-primary underline-offset-4 hover:underline"
              >
                {versao.versao}
              </Link>
              <span className="font-mono text-cell text-on-surface-muted">{versao.branch}</span>
              <StatusBuildBadge status={versao.status} />
              <span className="ml-auto text-cell text-on-surface-muted" title={`${versao.adicionado_por} em ${formatarDataHora(versao.adicionado_em)}`}>
                adicionada {formatarRelativo(versao.adicionado_em)}
              </span>
              {ultima ? null : escrita.permitido ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 px-0 text-on-surface-muted hover:text-danger"
                  aria-label={`Remover a versão ${versao.versao} do incidente`}
                  title="Remover da lista de versões afetadas"
                  onClick={() => {
                    setErroDialogo(null);
                    setRemovendo(versao.versao);
                  }}
                >
                  <X aria-hidden="true" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled
                  className="size-8 px-0"
                  aria-label={`Remover a versão ${versao.versao} do incidente: ${escrita.motivo}`}
                  title={escrita.motivo}
                >
                  <X aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
        {ultima ? (
          <p className="text-cell text-on-surface-muted">
            Um incidente precisa de pelo menos uma versão afetada; para tirar esta, apague o incidente.
          </p>
        ) : null}
      </section>

      <section aria-labelledby="inc-linha-do-tempo" className="flex flex-col gap-4">
        <h2 id="inc-linha-do-tempo" className="text-card-title">
          Linha do tempo
        </h2>
        <LinhaDoTempoIncidente eventos={incidente.eventos} />
      </section>

      <IncidenteDialog
        aberto={editando}
        incidente={incidente}
        aoFechar={() => setEditando(false)}
        aoSalvar={(detalhe) => {
          aoAtualizar(detalhe);
          setEditando(false);
        }}
      />

      <AdicionarVersoesDialog
        aberto={adicionando}
        incidente={incidente}
        aoFechar={() => setAdicionando(false)}
        aoConcluir={(detalhe, antes) => {
          atualizado(detalhe, antes);
          setAdicionando(false);
        }}
      />

      <ConfirmDialog
        aberto={removendo !== null}
        titulo={`Remover ${removendo ?? ''} do incidente?`}
        descricao="A versão deixa de ser afetada por este incidente e deixa de contar nos avisos dela. A remoção fica na linha do tempo."
        confirmar="Remover versão"
        perigo
        erro={erroDialogo}
        enviando={enviando}
        aoConfirmar={() => removendo && void remover(removendo)}
        aoFechar={() => setRemovendo(null)}
      />

      <ConfirmDialog
        aberto={apagando}
        titulo={`Apagar o incidente #${incidente.id}?`}
        descricao="O incidente, as associações com versões e todo o histórico dele são apagados de vez. Isso não pode ser desfeito."
        confirmar="Apagar incidente"
        perigo
        erro={erroDialogo}
        enviando={enviando}
        aoConfirmar={() => void apagarIncidente()}
        aoFechar={() => setApagando(false)}
      />
    </div>
  );
}

function AdicionarVersoesDialog({
  aberto,
  incidente,
  aoFechar,
  aoConcluir,
}: {
  aberto: boolean;
  incidente: IncidenteDetalhe;
  aoFechar: () => void;
  aoConcluir: (detalhe: IncidenteDetalhe, antes: string[]) => void;
}) {
  const [escolhidas, setEscolhidas] = React.useState<string[]>([]);
  const [erro, setErro] = React.useState<string | null>(null);
  const [enviando, setEnviando] = React.useState(false);

  React.useEffect(() => {
    if (aberto) {
      setEscolhidas([]);
      setErro(null);
    }
  }, [aberto]);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (escolhidas.length === 0) {
      setErro('Escolha pelo menos uma versão.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const antes = incidente.versoes.map((v) => v.versao);
      const detalhe = await dataSource.adicionarVersoesAoIncidente(incidente.id, escolhidas);
      toast.success(`${plural(escolhidas.length, 'versão adicionada', 'versões adicionadas')} ao incidente.`);
      aoConcluir(detalhe, antes);
    } catch (causa) {
      setErro(textoDoErro(causa));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && !enviando && aoFechar()}>
      <DialogContent className={cn('max-h-[calc(100dvh-2rem)] overflow-y-auto')}>
        <form onSubmit={enviar} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Adicionar versões ao incidente #{incidente.id}</DialogTitle>
            <DialogDescription>Versões já associadas aparecem desabilitadas.</DialogDescription>
          </DialogHeader>
          <VersoesSeletor
            id="incidente-add-versoes"
            valores={escolhidas}
            aoMudar={setEscolhidas}
            produto={incidente.produto}
            bloqueadas={incidente.versoes.map((v) => v.versao)}
          />
          {erro ? <Callout tom="erro">{erro}</Callout> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={enviando} disabled={escolhidas.length === 0}>
              Adicionar {escolhidas.length > 0 ? escolhidas.length : ''}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetalheCarregando() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Carregando o incidente">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-5 w-56" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="h-24" />
      <Skeleton className="h-40" />
    </div>
  );
}
